import React, { useMemo } from 'react';
import clsx from 'clsx';
import ReactSelect, { components } from 'react-select';

import selectStyles from './selectStyles';
import { colors } from '@utils/constants/colors';
import { BaseComponentProps } from '@utils/types/baseComponents';
import Checkbox from '@components/_shared/checkbox';

import styles from './styles.module.scss';

export type Option = {
  value: string | string[] | number | number[];
  label: string | JSX.Element;
  type?: string;
  default?: boolean;
};

type Props = {
  options: Option[];
  onSelect: (value: string | string[]) => void;
  value?: Option['value'] | null;
  multiple?: boolean;
  searchable?: boolean;
  clearable?: boolean;
  placeholder?: string;
  customEmptyValue?: Option;
  customKey?: string;
  withRoundCorners?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
} & BaseComponentProps;

const customComponents = {
  MultiValue: ({ selectProps, data }: any) => {
    const values = selectProps.value;
    if (values) {
      return values[values.length - 1].label === data.label
        ? data.label
        : data.label + ', ';
    } else return '';
  },
  ValueContainer: ({ children, ...props }: any) => {
    const { getValue, isMulti } = props;
    const nbValues = getValue().length;
    if (!isMulti || nbValues <= 1) {
      return (
        <components.ValueContainer {...props}>
          {children}
        </components.ValueContainer>
      );
    }
    return (
      <components.ValueContainer {...props}>
        {`${nbValues} selected`}
      </components.ValueContainer>
    );
  },
  Option: (props: any) => (
    <components.Option
      {...props}
      className={clsx([
        styles.selectOption,
        props.isMulti && styles.multiOption,
      ])}
    >
      {props.isMulti && (
        <Checkbox
          label=""
          value={
            props.isSelected || (!props.hasValue && props.data?.value === '')
          }
          containerStyle={styles.multiCheckbox}
        />
      )}
      <span>{props.data?.label}</span>
    </components.Option>
  ),
};

const Select = (props: Props) => {
  const {
    containerStyle,
    options,
    onSelect,
    placeholder = 'Select an option',
    multiple = false,
    value = undefined,
    searchable = false,
    clearable = false,
    customKey = 'simple-select',
    withRoundCorners = false,
    isLoading = false,
    isDisabled = false,
    customEmptyValue,
  } = props;

  const allOptions = useMemo(() => {
    if (customEmptyValue) return [customEmptyValue, ...options];
    return options;
  }, [options, customEmptyValue, placeholder]);

  const selected = useMemo(() => {
    if (!value) {
      return '';
    }
    if (typeof value === 'object') {
      return value.map((v) => options.find((o) => o.value === v));
    }
    return options.find((o) => o.value === value);
  }, [value]);

  const onChange = (option: any) => {
    if (multiple) {
      onSelect(
        option.some((o: Option) => o.value === '')
          ? []
          : option.map((o: Option) => o.value)
      );
      return;
    }
    onSelect(option.value);
  };

  return (
    <div className={clsx([styles.selectContainer, containerStyle])}>
      <ReactSelect<Option, typeof multiple>
        key={customKey}
        instanceId={'select'}
        isMulti={multiple}
        options={allOptions}
        onChange={onChange}
        noOptionsMessage={() => <>No options available</>}
        placeholder={placeholder}
        // @ts-ignore
        value={selected}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: colors.roveBlue,
          },
        })}
        styles={selectStyles(withRoundCorners)}
        isSearchable={searchable}
        isClearable={clearable}
        components={customComponents}
        hideSelectedOptions={false}
        closeMenuOnSelect={!multiple}
        withRoundCorners={withRoundCorners}
        isLoading={isLoading}
        isDisabled={isLoading || isDisabled}
      />
    </div>
  );
};

export default Select;
